import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi } from '../../lib/adminApi';

const emptyForm = {
  page_type: 'department',
  name: '',
  slug: '',
  parent_group: '',
  short_description: '',
  overview: '',
  vision: '',
  mission: '',
  head_name: '',
  head_title: '',
  head_photo_url: '',
  head_bio: '',
  head_quote: '',
  hero_image_url: '',
  email: '',
  phone: '',
  office_location: '',
  opening_hours: 'Monday - Friday, 8:00 AM - 4:00 PM',
  primary_cta_label: 'Contact Office',
  primary_cta_url: '#contact',
  secondary_cta_label: 'Explore Details',
  secondary_cta_url: '#offerings',
  status: 'draft',
  is_featured: false,
  meta_title: '',
  meta_description: '',
};

const officialSchools = [
  'School of Engineering',
  'School of Agricultural Technology',
  'School of Basic and General Studies',
  'School of Financial Studies',
  'School of Business Administration and Management',
  'School of Information Management Technology',
  'School of Computing Technology',
  'School of Applied & Natural Science',
  'School of Environmental Studies',
  'School of Communication and Media Studies',
];

function parentGroupLabel(pageType) {
  if (pageType === 'department') return 'School';
  if (pageType === 'center') return 'Centre Category / Parent Group';
  if (pageType === 'directorate') return 'Directorate Category / Parent Group';
  if (pageType === 'unit') return 'Unit Category / Parent Group';
  return 'Parent Group';
}

const defaultSections = {
  stats: [{ item_type: 'stat', title: 'Programmes', value: 'ND/HND', description: 'Academic pathways' }],
  quick_info: [{ item_type: 'quick_info', title: 'Location', value: 'Federal Polytechnic Bida' }],
  offerings: [{ item_type: 'offering', title: '', description: '', meta: '' }],
  features: [{ item_type: 'feature', title: '', description: '' }],
  timeline: [],
  downloads: [],
  gallery: [],
  faqs: [],
  admission: [],
  staff: [],
  course_year1: [],
  course_year2: [],
  career_networking: [],
  career_cloud: [],
  certification: [],
  brochure: [],
  hod_progress: [],
  map_point: [],
};

function slugify(text) {
  return String(text || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function itemTypeFromKey(key) {
  const map = {
    stats: 'stat',
    offerings: 'offering',
    features: 'feature',
    faqs: 'faq',
    admission: 'admission',
    staff: 'staff',
    course_year1: 'course_year1',
    course_year2: 'course_year2',
    career_networking: 'career_networking',
    career_cloud: 'career_cloud',
    certification: 'certification',
    brochure: 'brochure',
    hod_progress: 'hod_progress',
    map_point: 'map_point',
  };
  return map[key] || key;
}

function groupItems(items = []) {
  const grouped = JSON.parse(JSON.stringify(defaultSections));
  Object.keys(grouped).forEach((key) => { grouped[key] = []; });
  (items || []).forEach((item) => {
    const keyMap = { stat: 'stats', offering: 'offerings', feature: 'features', faq: 'faqs' };
    const key = keyMap[item.item_type] || item.item_type;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push({ ...item });
  });
  Object.keys(defaultSections).forEach((key) => {
    if (!grouped[key] || !grouped[key].length) grouped[key] = JSON.parse(JSON.stringify(defaultSections[key]));
  });
  return grouped;
}

function flattenItems(sections) {
  return Object.entries(sections).flatMap(([key, list]) =>
    (list || [])
      .filter((item) => item.title || item.value || item.description || item.url || item.image_url)
      .map((item, index) => ({ ...item, item_type: itemTypeFromKey(key), sort_order: index }))
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-sm font-black text-slate-700">{label}</span><div className="mt-2">{children}</div></label>;
}

function inputClass() {
  return 'w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-600';
}

function SectionEditor({ title, help, fields, items, onChange, addLabel = 'Add item' }) {
  function update(index, field, value) {
    const next = [...items];
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  }
  function add() {
    onChange([...items, { title: '', description: '', meta: '', value: '', url: '', image_url: '' }]);
  }
  function remove(index) {
    onChange(items.filter((_, i) => i !== index));
  }
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-lg font-black text-slate-950">{title}</h3>
          {help && <p className="mt-1 text-sm text-slate-500">{help}</p>}
        </div>
        <button type="button" onClick={add} className="rounded-xl border border-emerald-200 px-4 py-2 text-sm font-black text-emerald-700 hover:bg-emerald-50">+ {addLabel}</button>
      </div>
      <div className="space-y-4">
        {items.map((item, index) => (
          <div key={index} className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200">
            <div className="mb-3 flex justify-between gap-3">
              <span className="text-xs font-black uppercase tracking-wider text-slate-500">Item {index + 1}</span>
              <button type="button" onClick={() => remove(index)} className="text-xs font-black text-red-600">Remove</button>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {fields.includes('title') && <input value={item.title || ''} onChange={(e) => update(index, 'title', e.target.value)} placeholder="Title / Question / Label" className={inputClass()} />}
              {fields.includes('value') && <input value={item.value || ''} onChange={(e) => update(index, 'value', e.target.value)} placeholder="Value / Year" className={inputClass()} />}
              {fields.includes('meta') && <input value={item.meta || ''} onChange={(e) => update(index, 'meta', e.target.value)} placeholder="Meta / Badge" className={inputClass()} />}
              {fields.includes('url') && <input value={item.url || ''} onChange={(e) => update(index, 'url', e.target.value)} placeholder="URL / Download link" className={inputClass()} />}
              {fields.includes('image_url') && <input value={item.image_url || ''} onChange={(e) => update(index, 'image_url', e.target.value)} placeholder="Image URL" className={inputClass()} />}
            </div>
            {fields.includes('description') && <textarea value={item.description || ''} onChange={(e) => update(index, 'description', e.target.value)} placeholder="Description / Answer / Caption" rows="3" className={`${inputClass()} mt-3`} />}
          </div>
        ))}
        {!items.length && <p className="text-sm font-bold text-slate-500">No items added yet.</p>}
      </div>
    </div>
  );
}

export default function AdminInstitutionalPageForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => ({ ...emptyForm, page_type: searchParams.get('type') || 'department' }));
  const [sections, setSections] = useState(defaultSections);
  const [heroFile, setHeroFile] = useState(null);
  const [headFile, setHeadFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const publicPath = useMemo(() => {
    const plural = form.page_type === 'center' ? 'centers' : form.page_type === 'directorate' ? 'directorates' : form.page_type === 'unit' ? 'units' : 'departments';
    return form.slug ? `/${plural}/${form.slug}` : `/${plural}/new-page`;
  }, [form.page_type, form.slug]);

  useEffect(() => {
    if (!id) return;
    async function load() {
      try {
        const data = await adminApi(`/api/admin/institutional-pages/${id}`);
        setForm({
          ...emptyForm,
          ...data,
          is_featured: Boolean(data.is_featured),
          hero_image_url: data.hero_image || '',
          head_photo_url: data.head_photo || '',
        });
        setSections(groupItems(data.items || []));
      } catch (err) {
        setError(err.message);
      }
    }
    load();
  }, [id]);

  function updateField(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'name' && !id && !prev.slug) next.slug = slugify(value);
      if (name === 'page_type') {
        if (value === 'department') {
          next.primary_cta_label = next.primary_cta_label || 'Apply / Admissions';
          next.primary_cta_url = next.primary_cta_url || '/admissions';
        }
        if (value !== 'department' && officialSchools.includes(next.parent_group)) {
          next.parent_group = '';
        }
      }
      return next;
    });
  }

  function updateSection(key, value) {
    setSections((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(e) {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      if (form.page_type === 'department' && !String(form.parent_group || '').trim()) {
        setError('Please select the School this department belongs to. Departments must appear under a School in the public header.');
        setSaving(false);
        return;
      }
      const body = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value !== undefined && value !== null) body.append(key, value);
      });
      body.set('is_featured', form.is_featured ? '1' : '0');
      body.set('items', JSON.stringify(flattenItems(sections)));
      if (heroFile) body.append('hero_image', heroFile);
      if (headFile) body.append('head_photo', headFile);

      await adminApi(id ? `/api/admin/institutional-pages/${id}` : '/api/admin/institutional-pages', {
        method: id ? 'PUT' : 'POST',
        body,
      });
      navigate('/adxm/institutional-pages');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout
      title={id ? 'Edit Institutional Page' : 'Create Institutional Page'}
      subtitle="Use one beautiful template for departments, centers, directorates and units."
      actions={<Link to="/adxm/institutional-pages" className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-50">Back to list</Link>}
    >
      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>}
      <form onSubmit={submit} className="space-y-6">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-xl font-black text-slate-950">Basic Information</h3>
              <p className="text-sm text-slate-500">This controls the public title, route, hero and page status. Departments are grouped under Schools in the public header.</p>
            </div>
            <span className="rounded-full bg-emerald-50 px-4 py-2 text-xs font-black text-emerald-700">Preview path: {publicPath}</span>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Page Type"><select name="page_type" value={form.page_type} onChange={updateField} className={inputClass()}><option value="department">Department</option><option value="center">Center</option><option value="directorate">Directorate</option><option value="unit">Unit</option></select></Field>
            <Field label="Status"><select name="status" value={form.status} onChange={updateField} className={inputClass()}><option value="draft">Draft</option><option value="active">Published / Active</option><option value="inactive">Inactive</option></select></Field>
            <Field label="Name"><input name="name" value={form.name} onChange={updateField} required className={inputClass()} /></Field>
            <Field label="Slug"><input name="slug" value={form.slug} onChange={updateField} placeholder="auto-generated for new pages" className={inputClass()} /></Field>
            <Field label={parentGroupLabel(form.page_type)}>
              {form.page_type === 'department' ? (
                <>
                  <input name="parent_group" list="official-school-options" value={form.parent_group || ''} onChange={updateField} required placeholder="Select or type School name" className={inputClass()} />
                  <datalist id="official-school-options">
                    {officialSchools.map((school) => <option key={school} value={school} />)}
                  </datalist>
                  <p className="mt-2 text-xs font-semibold text-blue-700">This controls where the department appears in the public header: Academics → School → Department.</p>
                </>
              ) : (
                <>
                  <input name="parent_group" value={form.parent_group || ''} onChange={updateField} placeholder="Optional grouping/category" className={inputClass()} />
                  <p className="mt-2 text-xs font-semibold text-slate-500">Centres, directorates and units still appear under their own header sections.</p>
                </>
              )}
            </Field>
            <Field label="Featured"><label className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3"><input type="checkbox" name="is_featured" checked={form.is_featured} onChange={updateField} /> <span className="text-sm font-bold">Feature this page</span></label></Field>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Field label="Short Description"><textarea name="short_description" value={form.short_description || ''} onChange={updateField} rows="4" className={inputClass()} /></Field>
            <Field label="Overview"><textarea name="overview" value={form.overview || ''} onChange={updateField} rows="4" placeholder="Use blank line to separate paragraphs" className={inputClass()} /></Field>
            <Field label="Vision"><textarea name="vision" value={form.vision || ''} onChange={updateField} rows="3" className={inputClass()} /></Field>
            <Field label="Mission"><textarea name="mission" value={form.mission || ''} onChange={updateField} rows="3" className={inputClass()} /></Field>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Field label="Hero Image Upload"><input type="file" accept="image/*" onChange={(e) => setHeroFile(e.target.files?.[0] || null)} className={inputClass()} /></Field>
            <Field label="Or Hero Image URL"><input name="hero_image_url" value={form.hero_image_url || ''} onChange={updateField} className={inputClass()} /></Field>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-xl font-black text-slate-950">Leadership / Head Profile</h3>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <Field label="Head/HOD/Director/Coordinator Name"><input name="head_name" value={form.head_name || ''} onChange={updateField} className={inputClass()} /></Field>
            <Field label="Head Title"><input name="head_title" value={form.head_title || ''} onChange={updateField} placeholder="Head of Department, Director, Unit Coordinator..." className={inputClass()} /></Field>
            <Field label="Head Photo Upload"><input type="file" accept="image/*" onChange={(e) => setHeadFile(e.target.files?.[0] || null)} className={inputClass()} /></Field>
            <Field label="Or Head Photo URL"><input name="head_photo_url" value={form.head_photo_url || ''} onChange={updateField} className={inputClass()} /></Field>
            <Field label="Head Quote"><textarea name="head_quote" value={form.head_quote || ''} onChange={updateField} rows="3" className={inputClass()} /></Field>
            <Field label="Short Biography"><textarea name="head_bio" value={form.head_bio || ''} onChange={updateField} rows="3" className={inputClass()} /></Field>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-xl font-black text-slate-950">Contact, CTA and SEO</h3>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <Field label="Email"><input name="email" value={form.email || ''} onChange={updateField} className={inputClass()} /></Field>
            <Field label="Phone"><input name="phone" value={form.phone || ''} onChange={updateField} className={inputClass()} /></Field>
            <Field label="Office Location"><input name="office_location" value={form.office_location || ''} onChange={updateField} className={inputClass()} /></Field>
            <Field label="Opening Hours"><input name="opening_hours" value={form.opening_hours || ''} onChange={updateField} className={inputClass()} /></Field>
            <Field label="Primary Button Label"><input name="primary_cta_label" value={form.primary_cta_label || ''} onChange={updateField} className={inputClass()} /></Field>
            <Field label="Primary Button URL"><input name="primary_cta_url" value={form.primary_cta_url || ''} onChange={updateField} className={inputClass()} /></Field>
            <Field label="Secondary Button Label"><input name="secondary_cta_label" value={form.secondary_cta_label || ''} onChange={updateField} className={inputClass()} /></Field>
            <Field label="Secondary Button URL"><input name="secondary_cta_url" value={form.secondary_cta_url || ''} onChange={updateField} className={inputClass()} /></Field>
            <Field label="SEO Title"><input name="meta_title" value={form.meta_title || ''} onChange={updateField} className={inputClass()} /></Field>
            <Field label="SEO Description"><textarea name="meta_description" value={form.meta_description || ''} onChange={updateField} rows="3" className={inputClass()} /></Field>
          </div>
        </div>

        <SectionEditor title="Quick Statistics" help="Small hero cards like 3 Labs, ND/HND, 20 Staff." fields={['title', 'value', 'description']} items={sections.stats} onChange={(value) => updateSection('stats', value)} />
        <SectionEditor title="Quick Information" help="Office type, school, location, service area and other facts." fields={['title', 'value']} items={sections.quick_info} onChange={(value) => updateSection('quick_info', value)} />
        <SectionEditor title="Programmes / Services / Functions" help="For departments use programmes; for centers use services; for directorates and units use responsibilities/functions." fields={['title', 'description', 'meta']} items={sections.offerings} onChange={(value) => updateSection('offerings', value)} />
        <SectionEditor title="Impact and Strengths" fields={['title', 'description']} items={sections.features} onChange={(value) => updateSection('features', value)} />
        {form.page_type === 'department' && (
          <div className="rounded-3xl border border-blue-200 bg-blue-50/70 p-5 shadow-sm space-y-5">
            <div>
              <h3 className="text-xl font-black text-blue-950">Classic Department Page Sections</h3>
              <p className="mt-1 text-sm font-semibold text-blue-800">These fields make admin-created departments display like the original Networking & Cloud department page template.</p>
            </div>
            <SectionEditor title="Admission Requirements" help="Title can be Admission Requirements; description should contain the full requirement paragraph." fields={['title', 'description']} items={sections.admission} onChange={(value) => updateSection('admission', value)} addLabel="Add requirement" />
            <SectionEditor title="Department Staff" help="Title = staff name, Meta = role/designation, Image URL = staff photo, Description = short bio." fields={['title', 'meta', 'image_url', 'description']} items={sections.staff} onChange={(value) => updateSection('staff', value)} addLabel="Add staff" />
            <SectionEditor title="Course Structure - Year 1" help="Add each Year 1/core module as one item." fields={['title', 'description']} items={sections.course_year1} onChange={(value) => updateSection('course_year1', value)} addLabel="Add module" />
            <SectionEditor title="Course Structure - Year 2" help="Add each Year 2/advanced module as one item." fields={['title', 'description']} items={sections.course_year2} onChange={(value) => updateSection('course_year2', value)} addLabel="Add module" />
            <SectionEditor title="Job Prospects - Networking / Main Career Paths" help="Add career opportunities such as Network Engineer, System Administrator, Cybersecurity Specialist." fields={['title', 'description']} items={sections.career_networking} onChange={(value) => updateSection('career_networking', value)} addLabel="Add career" />
            <SectionEditor title="Job Prospects - Cloud / Specialist Opportunities" help="Add specialist careers such as Cloud Engineer, DevOps Engineer, Cloud Security Engineer." fields={['title', 'description']} items={sections.career_cloud} onChange={(value) => updateSection('career_cloud', value)} addLabel="Add career" />
            <SectionEditor title="Certification / Professional Courses" help="Title = certification name/provider, Meta = level/body, Description = benefit/details." fields={['title', 'meta', 'url', 'description']} items={sections.certification} onChange={(value) => updateSection('certification', value)} addLabel="Add certification" />
            <SectionEditor title="Admission Brochure Slides / Links" help="Title = brochure slide/resource title, URL = PDF or page link, Image URL = optional preview image." fields={['title', 'url', 'image_url', 'description']} items={sections.brochure} onChange={(value) => updateSection('brochure', value)} addLabel="Add brochure item" />
            <SectionEditor title="HOD Progress / Achievement Badges" help="Use this for the colourful HOD badges. Meta = icon/emoji such as 🏅, 📚, 🎓, ⭐, 🏆 or 🛡️. Title = achievement text e.g. 33 Conference Papers. Value is optional." fields={['title', 'value', 'meta', 'description']} items={sections.hod_progress} onChange={(value) => updateSection('hod_progress', value)} addLabel="Add HOD badge" />
            <SectionEditor title="Department Map / Location Points" help="Add office, laboratory or departmental location. URL can be a Google Maps embed/share link. Value can hold map coordinates or a short label. Description should hold the address." fields={['title', 'value', 'url', 'description']} items={sections.map_point} onChange={(value) => updateSection('map_point', value)} addLabel="Add map point" />
          </div>
        )}
        {form.page_type === 'directorate' && (
          <div className="rounded-3xl border border-indigo-200 bg-indigo-50/70 p-5 shadow-sm space-y-5">
            <div>
              <h3 className="text-xl font-black text-indigo-950">Classic Directorate Page Sections</h3>
              <p className="mt-1 text-sm font-semibold text-indigo-800">These fields make admin-created directorates display with the professional Directorate template, director progress badges and map section.</p>
            </div>
            <SectionEditor title="Director Progress / Achievement Badges" help="Use colourful badges for the directorate leader. Meta = icon/emoji such as 🏅, 📚, 🎓, ⭐, 🏆, 🛡️ or 💻. Title = achievement text, Value is optional." fields={['title', 'value', 'meta', 'description']} items={sections.hod_progress} onChange={(value) => updateSection('hod_progress', value)} addLabel="Add directorate badge" />
            <SectionEditor title="Directorate Map / Office Location Points" help="Add directorate office, service desk, server room, support center or other location points. URL can be a Google Maps embed/share link. Description should hold the address." fields={['title', 'value', 'url', 'description']} items={sections.map_point} onChange={(value) => updateSection('map_point', value)} addLabel="Add map point" />
          </div>
        )}
        {form.page_type === 'center' && (
          <div className="rounded-3xl border border-teal-200 bg-teal-50/70 p-5 shadow-sm space-y-5">
            <div>
              <h3 className="text-xl font-black text-teal-950">Classic Center Page Sections</h3>
              <p className="mt-1 text-sm font-semibold text-teal-800">These fields make admin-created centres display with a dedicated professional centre template, coordinator progress badges and map/location section.</p>
            </div>
            <SectionEditor title="Center Director / Coordinator Progress Badges" help="Use colourful badges for the center leader. Meta = icon/emoji such as ✨, 🏅, 📚, 🎓, ⭐, 🏆, 💡 or 🤝. Title = achievement text, Value is optional." fields={['title', 'value', 'meta', 'description']} items={sections.hod_progress} onChange={(value) => updateSection('hod_progress', value)} addLabel="Add center badge" />
            <SectionEditor title="Center Map / Location Points" help="Add center office, training room, clinic, lab, service desk or other location points. URL can be a Google Maps embed/share link. Description should hold the address." fields={['title', 'value', 'url', 'description']} items={sections.map_point} onChange={(value) => updateSection('map_point', value)} addLabel="Add map point" />
          </div>
        )}
        {form.page_type === 'unit' && (
          <div className="rounded-3xl border border-emerald-200 bg-emerald-50/70 p-5 shadow-sm space-y-5">
            <div>
              <h3 className="text-xl font-black text-emerald-950">Classic Unit Page Sections</h3>
              <p className="mt-1 text-sm font-semibold text-emerald-800">These fields make admin-created units display with a dedicated professional unit template, coordinator progress badges and map/location section.</p>
            </div>
            <SectionEditor title="Unit Head / Coordinator Progress Badges" help="Use colourful badges for the unit leader. Meta = icon/emoji such as 🛡️, 🏅, ⭐, 🏆, ✅ or 📋. Title = achievement text, Value is optional." fields={['title', 'value', 'meta', 'description']} items={sections.hod_progress} onChange={(value) => updateSection('hod_progress', value)} addLabel="Add unit badge" />
            <SectionEditor title="Unit Map / Office Location Points" help="Add unit office, service desk, complaint desk, registry desk or other location points. URL can be a Google Maps embed/share link. Description should hold the address." fields={['title', 'value', 'url', 'description']} items={sections.map_point} onChange={(value) => updateSection('map_point', value)} addLabel="Add map point" />
          </div>
        )}
        <SectionEditor title="Timeline / Milestones" fields={['title', 'value', 'description']} items={sections.timeline} onChange={(value) => updateSection('timeline', value)} addLabel="Add milestone" />
        <SectionEditor title="Downloads / Resources" fields={['title', 'url', 'description']} items={sections.downloads} onChange={(value) => updateSection('downloads', value)} addLabel="Add resource" />
        <SectionEditor title="Gallery" fields={['title', 'image_url', 'description']} items={sections.gallery} onChange={(value) => updateSection('gallery', value)} addLabel="Add gallery item" />
        <SectionEditor title="FAQ" fields={['title', 'description']} items={sections.faqs} onChange={(value) => updateSection('faqs', value)} addLabel="Add FAQ" />

        <div className="sticky bottom-4 z-20 rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur md:flex md:items-center md:justify-between">
          <p className="text-sm font-bold text-slate-600">Save as draft first, preview the public page, then publish when ready.</p>
          <div className="mt-3 flex gap-3 md:mt-0">
            <Link to="/adxm/institutional-pages" className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-black text-slate-700 hover:bg-slate-50">Cancel</Link>
            <button disabled={saving} className="rounded-xl bg-emerald-700 px-6 py-3 text-sm font-black text-white shadow hover:bg-emerald-800 disabled:opacity-60">{saving ? 'Saving...' : 'Save Page'}</button>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
}
